import moment from 'moment'
import { UPSERT_RAW_ALERTS_ACTION } from '@/services/api/driverscore/score'

export function createAlertActionForm(form = {}) {
  return {
    reason: form?.reason ?? '',
    others: form?.others ?? null,
  }
}

export function normalizeAlertNote(value, t) {
  switch (value) {
    case 'Tidak ada kejadian':
    case 'No Occurrences':
      return t('notification.text.notValid')
    case 'Terdeteksi sebagai kejadian lain':
    case 'Detected as another occurrence':
      return t('notification.text.wrongDetection')
    default:
      return value
  }
}

export function getAlertActionData({ actionType, value, note, form }) {
  switch (actionType) {
    case 'FalseAlert':
      return `${value}|${note || (form?.reason ? form.reason.trim() : '')}`
    case 'Feedback':
      return note
    case 'Resolve':
      return `${value}`
    default:
      return null
  }
}

export function getFeedbackFromActionData(data) {
  return data?.includes('|') ? data.split('|')[1] : data
}

export function createFalseAlertActionHandlers({
  openActionModal,
  markAsResolved,
  setFormOpen,
  setFalseAlertReason,
}) {
  return {
    recheck: () => openActionModal('recheck'),
    resolved: () => openActionModal('resolved'),
    resolve: () => markAsResolved(true, 'Resolve'),
    'open-form': () => setFormOpen(true),
    'set-reason': (reason) => setFalseAlertReason(reason),
    cancel: () => setFormOpen(false),
    'false-alert': () => openActionModal('falseAlert'),
  }
}

export function waitModalHidden(root, modalId) {
  return new Promise((resolve) => {
    const handler = (_event, hiddenModalId) => {
      if (hiddenModalId !== modalId) return

      root.$off('bv::modal::hidden', handler)
      resolve()
    }

    root.$on('bv::modal::hidden', handler)
  })
}

export function getAlertActionModalTitle(type, t) {
  switch (type) {
    case 'delete':
      return t('notification.text.deleteNote')
    case 'falseAlert':
      return t('notification.text.markFalseAlert')
    case 'recheck':
      return t('notification.text.checkAgain')
    case 'resolved':
      return t('notification.text.notificationCheckedReturn')
    default:
      return ''
  }
}

export function getAlertActionModalText(type, t) {
  switch (type) {
    case 'delete':
      return t('notification.text.modalDeleteText')
    case 'falseAlert':
      return t('notification.text.modalFalseText')
    case 'recheck':
      return t('notification.text.modalCheckText')
    case 'resolved':
      return t('notification.text.notificationCheckedReturnText')
    default:
      return ''
  }
}

export async function upsertRawAlertAction({ apollo, alert, actionType, data, onError }) {
  const response = await apollo.mutate({
    mutation: UPSERT_RAW_ALERTS_ACTION,
    variables: {
      input: {
        actionType,
        rawAlertId: [alert.rawAlertId],
        data,
        selectionMode: 'Selection',
        dateRange: {
          start: moment(alert.createdOn).subtract(1, 'second').toISOString(),
          end: moment(alert.createdOn).add(1, 'second').toISOString(),
        },
      },
    },
  })

  const result = response.data.upsertRawAlertsAction
  if (!result.isSuccessful) {
    onError?.(result.message)
    return null
  }

  return result.upsertRawAlertsAction
}

export function buildAlertUserLog(action, fullname, note = '') {
  return {
    typeField: action.actionType,
    data: action.data,
    createdOn: action.createdOn,
    userId: action.userId,
    user: {
      fullname,
    },
    ...(note ? { note } : {}),
  }
}

export function createAlertActionContext({
  apollo,
  store,
  alert,
  popupId,
  note = '',
  form = createAlertActionForm(),
  t,
  toast,
  track,
  fullname,
  trackLocation,
  alertName,
}) {
  return {
    apollo,
    store,
    alert,
    popupId,
    note,
    form,
    t,
    toast,
    track,
    fullname,
    trackLocation,
    alertName,
  }
}

function showValidationError(context, fieldKey) {
  const { t, toast } = context
  toast.error(`${t(fieldKey)} ${t('global.validation.required')}`)
}

function trackAlertAction(context, event) {
  context.track(event, {
    location: context.trackLocation,
    alert_name: context.alertName,
  })
}

function resetPopupFalseAlertState(context) {
  context.store.dispatch('notification/updatePopupNote', {
    id: context.popupId,
    note: '',
  })
  context.store.dispatch('notification/updatePopupForm', {
    id: context.popupId,
    formData: createAlertActionForm(),
  })
}

function addPopupUserLog(context, action, note = '') {
  context.store.dispatch('notification/addUserLog', {
    id: context.popupId,
    log: buildAlertUserLog(action, context.fullname, note),
  })
}

export async function saveFalseAlertAction(
  context,
  { isFalseAlert, actionType = 'FalseAlert' } = {}
) {
  if (isFalseAlert && !context.form?.reason?.trim()) {
    showValidationError(context, 'notification.text.otherReasons')
    return false
  }

  const action = await upsertRawAlertAction({
    apollo: context.apollo,
    alert: context.alert,
    actionType,
    data: getAlertActionData({
      actionType,
      value: isFalseAlert,
      note: context.note,
      form: context.form,
    }),
    onError: (message) => context.toast.error(message),
  })

  if (!action) return false

  const feedback = getFeedbackFromActionData(action.data)

  context.store.dispatch('notification/updatePopupFalseAlert', {
    id: context.popupId,
    lastUserFeedback: {
      createdOn: action.createdOn,
      feedback,
      userId: action.userId,
    },
    isFalseAlert,
  })
  resetPopupFalseAlertState(context)
  addPopupUserLog(context, action, feedback)

  if (!isFalseAlert) {
    context.store.dispatch('notification/updatePopupResolved', {
      id: context.popupId,
      isResolved: false,
    })
  }

  trackAlertAction(context, isFalseAlert ? 'Set as False Alert' : 'Reactive False Alert')
  context.toast.success(
    isFalseAlert
      ? context.t('notification.text.falseAlertSuccess')
      : context.t('notification.text.cancelFalseAlert')
  )

  return true
}

export async function saveAlertNoteAction(context, { isFalseAlert } = {}) {
  if (!context.note?.trim()) {
    showValidationError(context, 'global.object.note')
    return false
  }

  const action = await upsertRawAlertAction({
    apollo: context.apollo,
    alert: context.alert,
    actionType: 'Feedback',
    data: context.note,
    onError: (message) => context.toast.error(message),
  })

  if (!action) return false

  context.store.dispatch('notification/updatePopupFalseAlert', {
    id: context.popupId,
    lastUserFeedback: {
      createdOn: action.createdOn,
      feedback: context.note,
      userId: action.userId,
    },
    isFalseAlert,
  })
  addPopupUserLog(context, action)

  trackAlertAction(context, 'Save Notification Notes')
  context.toast.success(context.t('global.alert.text.saved'))

  return true
}

export async function saveAlertResolveAction(
  context,
  { status, actionType = 'Resolve', trackEvent } = {}
) {
  const action = await upsertRawAlertAction({
    apollo: context.apollo,
    alert: context.alert,
    actionType,
    data: getAlertActionData({
      actionType,
      value: status,
      note: context.note,
      form: context.form,
    }),
    onError: (message) => context.toast.error(message),
  })

  if (!action) return false

  context.store.dispatch('notification/updatePopupResolved', {
    id: context.popupId,
    isResolved: status,
  })
  addPopupUserLog(context, action)

  context.track(trackEvent)
  context.toast.success(
    status
      ? context.t('notification.text.notificationChecked')
      : context.t('notification.text.notificationUnchecked')
  )

  return true
}
